const FRAME_EDGE_GAP = 16;
const FRAME_DEFAULT_WIDTH = 460;
const FRAME_DEFAULT_HEIGHT = 680;
const FRAME_MAX_WIDTH = 860;
const FRAME_MAX_HEIGHT = 860;
const FRAME_MIN_WIDTH = 320;
const FRAME_MIN_HEIGHT = 300;
const FRAME_CANCEL_OFFSET = 40;
const OPPOSING_FRAME_EDGES = 2;

export interface ThreeDSFrameSize {
  width?: number;
  height?: number;
}

export interface ThreeDSFrameShell {
  shell: HTMLDivElement;
  iframe: HTMLIFrameElement;
  cancelButton: HTMLButtonElement;
}

function getViewportSize(): { width: number; height: number } {
  const visualViewport = typeof window !== 'undefined' ? window.visualViewport : undefined;
  const documentElement = typeof document !== 'undefined' ? document.documentElement : undefined;

  return {
    width:
      visualViewport?.width ||
      window.innerWidth ||
      documentElement?.clientWidth ||
      window.screen?.width ||
      FRAME_DEFAULT_WIDTH,
    height:
      visualViewport?.height ||
      window.innerHeight ||
      documentElement?.clientHeight ||
      window.screen?.height ||
      FRAME_DEFAULT_HEIGHT,
  };
}

function clampToAvailableSpace(value: number, min: number, max: number): number {
  const lower = Math.min(min, max);
  return Math.min(Math.max(value, lower), max);
}

export function createThreeDSModalBackdrop(id: string): HTMLDivElement {
  const modal = document.createElement('div');
  const viewport = getViewportSize();
  modal.id = id;
  Object.assign(modal.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: `${viewport.width}px`,
    height: `${viewport.height}px`,
    minHeight: `${viewport.height}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '999999',
    background: 'rgba(0, 0, 0, 0.56)',
    padding: `${FRAME_EDGE_GAP}px`,
    boxSizing: 'border-box',
    overflow: 'hidden',
  });

  return modal;
}

export function applyThreeDSFrameSize(
  modal: HTMLDivElement | null,
  iframe: HTMLIFrameElement | null,
  requestedSize: ThreeDSFrameSize = {},
  shell?: HTMLDivElement | null,
): ThreeDSFrameSize {
  if (!modal || !iframe) {
    return requestedSize;
  }

  const viewport = getViewportSize();
  const maxWidth = Math.min(
    FRAME_MAX_WIDTH,
    Math.max(0, viewport.width - FRAME_EDGE_GAP * OPPOSING_FRAME_EDGES),
  );
  const maxHeight = Math.min(
    FRAME_MAX_HEIGHT,
    Math.max(0, viewport.height - (FRAME_EDGE_GAP + FRAME_CANCEL_OFFSET) * OPPOSING_FRAME_EDGES),
  );
  const requestedWidth = requestedSize.width ?? FRAME_DEFAULT_WIDTH;
  const requestedHeight = requestedSize.height ?? FRAME_DEFAULT_HEIGHT;
  const width = clampToAvailableSpace(requestedWidth, FRAME_MIN_WIDTH, maxWidth);
  const height = clampToAvailableSpace(requestedHeight, FRAME_MIN_HEIGHT, maxHeight);

  modal.style.width = `${viewport.width}px`;
  modal.style.height = `${viewport.height}px`;
  modal.style.minHeight = `${viewport.height}px`;
  iframe.style.width = `${Math.round(width)}px`;
  iframe.style.height = `${Math.round(height)}px`;
  if (shell) {
    shell.style.width = `${Math.round(width)}px`;
    shell.style.height = `${Math.round(height)}px`;
  }

  return { width, height };
}

export function createThreeDSFrameShell(onCancel: () => void): ThreeDSFrameShell {
  const shell = document.createElement('div');
  Object.assign(shell.style, {
    position: 'relative',
    borderRadius: '6px',
    background: 'transparent',
    boxShadow: '0 24px 64px rgba(0, 0, 0, 0.36), 0 2px 10px rgba(0, 0, 0, 0.18)',
    overflow: 'visible',
  });

  const iframe = document.createElement('iframe');
  iframe.allow = 'payment *';
  iframe.setAttribute('frameborder', '0');
  Object.assign(iframe.style, {
    border: 'none',
    outline: 'none',
    borderRadius: '6px',
    display: 'block',
    background: 'transparent',
    maxWidth: '100%',
    maxHeight: '100%',
  });

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.textContent = 'Cancel';
  cancelButton.setAttribute('aria-label', 'Cancel 3D Secure authentication');
  Object.assign(cancelButton.style, {
    position: 'absolute',
    top: '-40px',
    right: '0',
    zIndex: '2',
    height: '28px',
    padding: '0 10px',
    border: '0',
    borderRadius: '999px',
    background: 'rgba(17, 24, 39, 0.56)',
    color: '#ffffff',
    font: '600 11px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.24)',
    transform: 'translateY(0) scale(1)',
    transition: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      ? 'none'
      : 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 300ms cubic-bezier(0.22, 1, 0.36, 1), background 300ms cubic-bezier(0.22, 1, 0.36, 1)',
    willChange: 'transform',
  });
  const resetCancelButton = () => {
    cancelButton.style.background = 'rgba(17, 24, 39, 0.56)';
    cancelButton.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.24)';
    cancelButton.style.transform = 'translateY(0) scale(1)';
  };
  const liftCancelButton = () => {
    cancelButton.style.background = 'rgba(17, 24, 39, 0.68)';
    cancelButton.style.boxShadow = '0 8px 22px rgba(0, 0, 0, 0.26)';
    cancelButton.style.transform = 'translateY(-1px) scale(1.025)';
  };
  const pressCancelButton = () => {
    cancelButton.style.background = 'rgba(17, 24, 39, 0.72)';
    cancelButton.style.boxShadow = '0 3px 12px rgba(0, 0, 0, 0.22)';
    cancelButton.style.transform = 'translateY(0) scale(0.985)';
  };
  cancelButton.addEventListener('mouseenter', liftCancelButton);
  cancelButton.addEventListener('focus', liftCancelButton);
  cancelButton.addEventListener('mouseleave', resetCancelButton);
  cancelButton.addEventListener('blur', resetCancelButton);
  cancelButton.addEventListener('mouseup', liftCancelButton);
  cancelButton.addEventListener('mousedown', pressCancelButton);
  cancelButton.addEventListener('click', onCancel);

  shell.appendChild(iframe);
  shell.appendChild(cancelButton);

  return { shell, iframe, cancelButton };
}

export function normalizeThreeDSFrameSize(data: unknown): ThreeDSFrameSize | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const raw = data as { width?: unknown; height?: unknown };
  const next: ThreeDSFrameSize = {};

  if (typeof raw.width === 'number' && Number.isFinite(raw.width) && raw.width > 0) {
    next.width = raw.width;
  }

  if (typeof raw.height === 'number' && Number.isFinite(raw.height) && raw.height > 0) {
    next.height = raw.height;
  }

  return next.width || next.height ? next : null;
}
